import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import CompanyPecBanner from "../components/CompanyPecBanner";
import LoadingState from "../components/LoadingState";
import EmptyState from "../components/EmptyState";
import AlertBanner from "../components/AlertBanner";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck, Trash2, Plus, PlugZap, ChevronDown, ChevronUp, Info, AlertTriangle, RotateCcw } from "lucide-react";
import { useAuth } from "../lib/auth";

const ENCRYPTIONS = ["SSL", "STARTTLS", "NONE"];

const STATUS_LABEL_IT = {
    success: "Connessione riuscita",
    credentials_invalid: "Credenziali IMAP non valide",
    host_unreachable: "Server IMAP non raggiungibile",
    certificate_invalid: "Certificato del server non valido",
    port_blocked: "Porta IMAP bloccata",
    folder_not_found: "Cartella non trovata sul server",
    imap_response_invalid: "Risposta IMAP non valida",
    timeout: "Timeout di connessione",
    auth_disabled: "Autenticazione rifiutata dal server",
};

function statusColor(s) {
    if (s === "Connessa" || s === STATUS_LABEL_IT.success) return "bg-[#16A34A]/10 text-[#166534] border border-[#16A34A]/30";
    if (!s || s === "Non verificata") return "bg-slate-100 text-slate-600 border border-slate-200";
    return "bg-[#DC2626]/10 text-[#991B1B] border border-[#DC2626]/30";
}

const EMPTY_FORM = {
    display_name: "", pec_address: "", imap_host: "", imap_port: 993,
    encryption: "SSL", username: "", password: "", folder: "INBOX",
    sync_frequency_minutes: 15,
    provider_slug: "",         // BLOCCO 5: provider preset
    use_real_adapter: true,    // BLOCCO 5: default real
    initial_sync_days: 15,     // Task 1: first sync history window (0-90; 0 = full history)
};

function formatDateIT(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    } catch { return iso.slice(0, 10); }
}

export default function MailboxesPage() {
    const canAdmin = useAuth((s) => s.canAdmin);
    const companyId = useAuth((s) => s.companyId);
    const companies = useAuth((s) => s.companies);
    const isAdmin = canAdmin();
    // A "specific company" (not the "__all__" sentinel) is required to create
    // a mailbox — the backend rejects POST /mailboxes when X-Company-Id is
    // "__all__" with 400 "Selezionare un'azienda specifica". We surface this
    // at the UI level so the user cannot even open a form that will fail.
    const isGlobalMode = companyId === "__all__";
    const currentCompany = (companies || []).find((c) => c.id === companyId);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [saving, setSaving] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [logs, setLogs] = useState({});
    const [logsFor, setLogsFor] = useState(null);
    const [providerPresets, setProviderPresets] = useState([]);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [forceModal, setForceModal] = useState(null); // { id, display_name, initial_sync_days }
    const [forceReason, setForceReason] = useState("");
    const [forceSaving, setForceSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/mailboxes");
            setItems(data.items || []);
        } catch { toast.error("Errore caricamento"); }
        finally { setLoading(false); }
    };

    const loadPresets = async () => {
        try {
            const { data } = await api.get("/company/pec-providers-presets");
            setProviderPresets(data.items || []);
        } catch { /* silent */ }
    };

    useEffect(() => { load(); loadPresets(); }, []);

    const openForm = () => {
        if (isGlobalMode) {
            // Backend will reject anyway; block early with a clear message.
            toast.error("Seleziona una società specifica dalla barra laterale per creare una casella PEC.");
            return;
        }
        setForm({ ...EMPTY_FORM });
        setAdvancedOpen(false);
        setShowForm(true);
    };

    const onProviderChange = (slug) => {
        if (!slug) {
            setForm((prev) => ({ ...prev, provider_slug: "" }));
            return;
        }
        const preset = providerPresets.find((p) => p.slug === slug);
        if (!preset) return;
        const s = preset.suggested_imap || {};
        const firstDomain = (preset.domains && preset.domains[0]) ? `@${preset.domains[0]}` : "";
        setForm((prev) => ({
            ...prev,
            provider_slug: slug,
            imap_host: s.host || prev.imap_host,
            imap_port: s.port || 993,
            encryption: s.encryption || "SSL",
            // Suggest but do not overwrite user-typed pec_address
            pec_address: prev.pec_address || firstDomain,
        }));
    };

    const create = async () => {
        if (!form.display_name || !form.pec_address || !form.imap_host || !form.username || !form.password) {
            toast.error("Compila tutti i campi obbligatori");
            return;
        }
        const isd = Number(form.initial_sync_days);
        if (!Number.isInteger(isd) || isd < 0 || isd > 90) {
            toast.error("I giorni di storico devono essere un intero tra 0 e 90");
            return;
        }
        setSaving(true);
        try {
            await api.post("/mailboxes", form);
            toast.success("Casella creata");
            setShowForm(false);
            setForm({ ...EMPTY_FORM });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };

    const openForceModal = (m) => {
        setForceReason("");
        setForceModal({ id: m.id, display_name: m.display_name, initial_sync_days: m.initial_sync_days ?? 15 });
    };

    const confirmForceInitialSync = async () => {
        if (!forceModal) return;
        const reason = forceReason.trim();
        if (reason.length < 10) {
            toast.error("Motivazione obbligatoria (minimo 10 caratteri)");
            return;
        }
        setForceSaving(true);
        try {
            await api.post(`/mailboxes/${forceModal.id}/force-initial-sync`, { reason });
            toast.success("Primo sync azzerato. Il prossimo sync ricaricherà lo storico.");
            setForceModal(null);
            setForceReason("");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally {
            setForceSaving(false);
        }
    };

    const verify = async (id) => {
        setBusyId(id);
        try {
            const { data } = await api.post(`/mailboxes/${id}/verify`);
            if (data.ok) toast.success(`Connessione OK · ${(data.folders || []).length} cartelle rilevate`);
            else toast.error(data.message_it || STATUS_LABEL_IT[data.status] || "Errore di verifica");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusyId(null); }
    };

    const sync = async (id) => {
        setBusyId(id);
        try {
            const { data } = await api.post(`/mailboxes/${id}/sync`);
            toast.success(`Sync completata: ${data.new || 0} nuove PEC`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setBusyId(null); }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminare la casella?")) return;
        try { await api.delete(`/mailboxes/${id}`); toast.success("Eliminata"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    };

    const viewLogs = async (id) => {
        setLogsFor(logsFor === id ? null : id);
        if (logsFor === id) return;
        try {
            const { data } = await api.get(`/mailboxes/${id}/sync-logs?limit=20`);
            setLogs({ ...logs, [id]: data.items || [] });
        } catch { toast.error("Errore log"); }
    };

    if (loading) return <LoadingState label="Caricamento caselle PEC…" />;

    return (
        <div className="space-y-4" data-testid="mailboxes-page">
            <PageHeader
                title="Caselle PEC"
                subtitle="Configurazione e sincronizzazione delle caselle IMAP aziendali."
                breadcrumbs={[{ label: "Integrazioni" }, { label: "Caselle PEC" }]}
                actions={
                    <button
                        onClick={openForm}
                        disabled={isGlobalMode}
                        data-testid="new-mailbox-btn"
                        title={isGlobalMode ? "Seleziona una società specifica per aggiungere una casella" : undefined}
                        className={`inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg text-white ${isGlobalMode ? "bg-[#94A3B8] cursor-not-allowed" : "bg-[#2563EB] hover:bg-[#1D4ED8]"}`}
                    >
                        <Plus className="w-4 h-4" /> Nuova casella PEC
                    </button>
                }
            />
            <CompanyPecBanner />

            {isGlobalMode && (
                <AlertBanner
                    variant="attenzione"
                    title="Seleziona una società per aggiungere una casella"
                    testId="mailboxes-select-company-banner"
                >
                    Stai vedendo <span className="font-medium">tutte le caselle PEC</span> aggregate.
                    Per creare o modificare una casella devi prima selezionare una società specifica
                    dalla barra laterale sinistra.
                </AlertBanner>
            )}

            {items.length === 0 ? (
                <EmptyState
                    title="Nessuna casella configurata"
                    description="Aggiungi la prima casella PEC per iniziare la sincronizzazione automatica."
                />
            ) : (
                <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                    <table className="w-full text-sm" data-testid="mailboxes-table">
                        <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                            <tr>
                                <th className="text-left px-4 py-3">Nome</th>
                                <th className="text-left px-4 py-3">Indirizzo PEC</th>
                                <th className="text-left px-4 py-3">Server</th>
                                <th className="text-left px-4 py-3">Stato</th>
                                <th className="text-left px-4 py-3">Ultimo sync</th>
                                <th className="text-left px-4 py-3">Azioni</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#DCE3EE]">
                            {items.map((m) => (
                                <React.Fragment key={m.id}>
                                    <tr data-testid={`mailbox-row-${m.id}`}>
                                        <td className="px-4 py-3 font-medium">
                                            {m.display_name}
                                            {m.use_real_adapter === false && (
                                                <span
                                                    data-testid={`mailbox-mock-badge-${m.id}`}
                                                    className="ml-2 inline-block bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5 text-[9px] uppercase font-bold"
                                                >
                                                    Mock
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#64748B]">{m.pec_address}</td>
                                        <td className="px-4 py-3 text-xs">
                                            <div>{m.imap_host}:{m.imap_port} ({m.encryption})</div>
                                            <div
                                                className="mt-1 text-[10px] text-[#64748B]"
                                                data-testid={`mailbox-initial-sync-info-${m.id}`}
                                            >
                                                Primo sync: {
                                                    m.first_sync_completed_at
                                                        ? `completato il ${formatDateIT(m.first_sync_completed_at)}`
                                                        : "non ancora eseguito"
                                                }
                                                {" · "}
                                                storico:{" "}
                                                {Number(m.initial_sync_days ?? 15) === 0
                                                    ? "tutto"
                                                    : `${m.initial_sync_days ?? 15} gg`}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-[11px] ${statusColor(m.connection_status)}`}>
                                                {m.connection_status || "Non verificata"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-[#64748B]">
                                            {m.last_sync_at ? m.last_sync_at.slice(0, 16).replace("T", " ") : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <button data-testid={`mailbox-verify-${m.id}`} onClick={() => verify(m.id)}
                                                    disabled={busyId === m.id}
                                                    className="text-xs text-[#2563EB] hover:underline flex items-center gap-1">
                                                    <ShieldCheck className="w-3.5 h-3.5" /> Verifica
                                                </button>
                                                <button data-testid={`mailbox-sync-${m.id}`} onClick={() => sync(m.id)}
                                                    disabled={busyId === m.id}
                                                    className="text-xs text-[#16A34A] hover:underline flex items-center gap-1">
                                                    <RefreshCw className="w-3.5 h-3.5" /> Sync
                                                </button>
                                                <button data-testid={`mailbox-logs-${m.id}`} onClick={() => viewLogs(m.id)}
                                                    className="text-xs text-[#64748B] hover:underline">Log</button>
                                                {isAdmin && (
                                                    <button
                                                        data-testid={`mailbox-force-initial-sync-${m.id}`}
                                                        onClick={() => openForceModal(m)}
                                                        disabled={busyId === m.id}
                                                        title="Azzera il primo sync e reimporta lo storico secondo initial_sync_days"
                                                        className="text-xs text-[#B45309] hover:underline flex items-center gap-1"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" />
                                                        {m.first_sync_completed_at ? "Ri-esegui primo sync" : "Forza primo sync"}
                                                    </button>
                                                )}
                                                <button data-testid={`mailbox-delete-${m.id}`} onClick={() => remove(m.id)}
                                                    className="text-xs text-[#DC2626] hover:underline flex items-center gap-1">
                                                    <Trash2 className="w-3.5 h-3.5" /> Elimina
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {logsFor === m.id && (
                                        <tr>
                                            <td colSpan="6" className="bg-[#F5F7FB] px-4 py-3">
                                                <div className="text-xs font-semibold mb-2">Log sincronizzazioni</div>
                                                <div className="space-y-1 max-h-40 overflow-y-auto" data-testid={`mailbox-logs-${m.id}-body`}>
                                                    {(logs[m.id] || []).length === 0 && <div className="text-xs text-[#64748B]">Nessun log</div>}
                                                    {(logs[m.id] || []).map((l) => (
                                                        <div key={l.id} className="text-xs">
                                                            {(l.finished_at || l.started_at || "").slice(0, 19).replace("T", " ")} · {l.status} · nuovi: {l.messages_new || 0} · fetch: {l.messages_fetched || 0}
                                                            {l.error_detail && <span className="text-[#DC2626]"> — {l.error_detail}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={() => setShowForm(false)}>
                    <div className="bg-white rounded-xl w-full max-w-xl p-6" onClick={(e) => e.stopPropagation()}
                        data-testid="mailbox-form-modal">
                        <h3 className="text-base font-semibold mb-4 text-[#172033]">Nuova casella PEC</h3>

                        {/* Provider preset dropdown */}
                        <div className="mb-4">
                            <label className="text-[11px] text-[#64748B] font-medium">Gestore PEC</label>
                            <select
                                data-testid="mailbox-form-provider"
                                value={form.provider_slug}
                                onChange={(e) => onProviderChange(e.target.value)}
                                className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1 bg-white"
                            >
                                <option value="">Seleziona un gestore per compilare automaticamente…</option>
                                {providerPresets.map((p) => (
                                    <option key={p.slug} value={p.slug} data-testid={`mailbox-form-provider-option-${p.slug}`}>
                                        {p.name}
                                        {p.suggested_imap ? ` — ${p.suggested_imap.host}:${p.suggested_imap.port}` : ""}
                                    </option>
                                ))}
                                <option value="__manual__" data-testid="mailbox-form-provider-option-manual">
                                    Altro (configurazione manuale)
                                </option>
                            </select>
                            <p className="text-[10px] text-[#64748B] mt-1 flex items-center gap-1">
                                <Info className="w-3 h-3" />
                                I valori suggeriti restano modificabili. Puoi personalizzarli se hai una configurazione speciale.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Nome descrittivo *</label>
                                <input data-testid="mailbox-form-display_name" type="text" value={form.display_name}
                                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                                    placeholder="es. Amministrazione"
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Indirizzo PEC *</label>
                                <input data-testid="mailbox-form-pec_address" type="email" value={form.pec_address}
                                    onChange={(e) => setForm({ ...form, pec_address: e.target.value })}
                                    placeholder="azienda@pec.aruba.it"
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-[11px] text-[#64748B] font-medium">Server IMAP *</label>
                                <input data-testid="mailbox-form-imap_host" type="text" value={form.imap_host}
                                    onChange={(e) => setForm({ ...form, imap_host: e.target.value })}
                                    placeholder="imaps.pec.aruba.it"
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Porta</label>
                                <input data-testid="mailbox-form-imap_port" type="number" value={form.imap_port}
                                    onChange={(e) => setForm({ ...form, imap_port: Number(e.target.value) })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Cifratura</label>
                                <select data-testid="mailbox-form-encryption" value={form.encryption}
                                    onChange={(e) => setForm({ ...form, encryption: e.target.value })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1 bg-white">
                                    {ENCRYPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Username *</label>
                                <input data-testid="mailbox-form-username" type="text" value={form.username}
                                    onChange={(e) => setForm({ ...form, username: e.target.value })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Password *</label>
                                <input data-testid="mailbox-form-password" type="password" value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Cartella</label>
                                <input data-testid="mailbox-form-folder" type="text" value={form.folder}
                                    onChange={(e) => setForm({ ...form, folder: e.target.value })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div>
                                <label className="text-[11px] text-[#64748B] font-medium">Frequenza sync (min)</label>
                                <input data-testid="mailbox-form-sync_frequency_minutes" type="number" value={form.sync_frequency_minutes}
                                    onChange={(e) => setForm({ ...form, sync_frequency_minutes: Number(e.target.value) })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1" />
                            </div>
                            <div className="col-span-2">
                                <label className="text-[11px] text-[#64748B] font-medium">Giorni di storico da importare al primo sync</label>
                                <input
                                    data-testid="mailbox-form-initial_sync_days"
                                    type="number"
                                    min={0}
                                    max={90}
                                    value={form.initial_sync_days}
                                    onChange={(e) => setForm({ ...form, initial_sync_days: Number(e.target.value) })}
                                    className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1"
                                />
                                <p className="text-[10px] text-[#64748B] mt-1">
                                    Default 15 giorni · massimo 90 · 0 = importa tutto lo storico.
                                    Il vincolo si applica solo al primo sync (SEARCH SINCE lato server IMAP).
                                </p>
                                {Number(form.initial_sync_days) === 0 && (
                                    <div
                                        data-testid="mailbox-form-initial_sync_days-warning"
                                        className="mt-2 flex items-start gap-2 border border-[#F59E0B]/40 bg-[#F59E0B]/10 rounded-md px-3 py-2"
                                    >
                                        <AlertTriangle className="w-3.5 h-3.5 text-[#B45309] mt-0.5 shrink-0" />
                                        <span className="text-[11px] text-[#78350F] leading-snug">
                                            Attenzione: può richiedere ore su mailbox grandi. Verranno scaricate tutte
                                            le PEC storiche presenti sul server.
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Advanced options */}
                        <button
                            type="button"
                            data-testid="mailbox-advanced-toggle"
                            onClick={() => setAdvancedOpen(!advancedOpen)}
                            className="mt-4 text-xs text-[#64748B] hover:text-[#172033] flex items-center gap-1"
                        >
                            {advancedOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            Opzioni avanzate
                        </button>
                        {advancedOpen && (
                            <div className="mt-2 border border-[#DCE3EE] rounded-md p-3 bg-[#F5F7FB]/50">
                                <label className="flex items-start gap-2 text-xs cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="mailbox-use-real-adapter-toggle"
                                        checked={!form.use_real_adapter}
                                        onChange={(e) => setForm({ ...form, use_real_adapter: !e.target.checked })}
                                        className="mt-0.5"
                                    />
                                    <span>
                                        <span className="font-medium text-[#172033]">Usa adapter di test (solo per debug)</span>
                                        <div className="text-[10px] text-[#64748B] mt-0.5">
                                            Se selezionato, la casella riceverà email fittizie invece di collegarsi al server IMAP reale.
                                            Consigliato solo in ambiente di test.
                                        </div>
                                    </span>
                                </label>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mt-6">
                            <button onClick={() => setShowForm(false)} className="text-xs px-3 py-2 rounded-md border border-[#DCE3EE] hover:bg-[#F5F7FB]">
                                Annulla
                            </button>
                            <button data-testid="mailbox-form-submit" onClick={create} disabled={saving}
                                className="text-xs px-3 py-2 rounded-md bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:opacity-50">
                                {saving ? "Salvataggio…" : "Crea casella"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Force initial sync modal */}
            {forceModal && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={() => !forceSaving && setForceModal(null)}
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-md p-6"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="mailbox-force-initial-sync-modal"
                    >
                        <div className="flex items-start gap-3 mb-3">
                            <div className="p-2 rounded-full bg-[#F59E0B]/10 text-[#B45309]">
                                <AlertTriangle className="w-4 h-4" />
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-[#172033]">Ri-esegui primo sync</h3>
                                <p className="text-xs text-[#64748B] mt-1">
                                    Verrà azzerato lo stato del primo sync di{" "}
                                    <span className="font-medium text-[#172033]">{forceModal.display_name}</span>.
                                    Al prossimo sync verranno reimportate le PEC degli ultimi{" "}
                                    <span className="font-medium">
                                        {Number(forceModal.initial_sync_days) === 0
                                            ? "tutto lo storico"
                                            : `${forceModal.initial_sync_days} giorni`}
                                    </span>.
                                </p>
                            </div>
                        </div>

                        <label className="text-[11px] text-[#64748B] font-medium">
                            Motivazione (min. 10 caratteri, salvata in audit log) *
                        </label>
                        <textarea
                            data-testid="mailbox-force-initial-sync-reason"
                            value={forceReason}
                            onChange={(e) => setForceReason(e.target.value)}
                            rows={3}
                            placeholder="Es. Richiesto dall'amministratore per recuperare le PEC del mese scorso"
                            className="w-full text-sm border border-[#DCE3EE] rounded-md px-3 py-2 mt-1"
                        />
                        <p className="text-[10px] text-[#64748B] mt-1">
                            {forceReason.trim().length}/10 caratteri minimi
                        </p>

                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                data-testid="mailbox-force-initial-sync-cancel"
                                onClick={() => setForceModal(null)}
                                disabled={forceSaving}
                                className="text-xs px-3 py-2 rounded-md border border-[#DCE3EE] hover:bg-[#F5F7FB]"
                            >
                                Annulla
                            </button>
                            <button
                                data-testid="mailbox-force-initial-sync-confirm"
                                onClick={confirmForceInitialSync}
                                disabled={forceSaving || forceReason.trim().length < 10}
                                className="text-xs px-3 py-2 rounded-md bg-[#B45309] text-white hover:bg-[#92400E] disabled:opacity-50"
                            >
                                {forceSaving ? "In corso…" : "Conferma e ri-esegui"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
